import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import PublicFooter from "../components/PublicFooter";
import { isDayOpen } from "../lib/businessHours";

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") + "/api";

const publicFetch = async (path, opts = {}) => {
    const res = await fetch(`${API_BASE}${path}`, {
        headers: { "Content-Type": "application/json", ...(opts.headers || {}) },
        ...opts,
    });
    let payload = null;
    try { payload = await res.json(); } catch (_) { /* empty */ }
    if (!res.ok) {
        const msg = payload?.detail || `Erro ${res.status}`;
        throw new Error(typeof msg === "string" ? msg : "Erro ao processar solicitação");
    }
    return payload;
};

// ISO local (YYYY-MM-DD) — NÃO usar toISOString() (converte pra UTC e, em
// UTC-3 depois das 21h, joga a data pro dia seguinte, desalinhando o rótulo
// do dia da semana em relação à data enviada ao backend).
const pad2 = (n) => String(n).padStart(2, "0");
const toISO = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const todayISO = () => toISO(new Date());
const nextDays = (n = 14) => {
    const list = [];
    const base = new Date();
    for (let i = 0; i < n; i++) {
        const d = new Date(base);
        d.setDate(base.getDate() + i);
        list.push(d);
    }
    return list;
};
const fmtDayShort = (d) => {
    const dow = ["Dom","Seg","Ter","Qua","Qui","Sex","Sáb"][d.getDay()];
    return `${dow} · ${String(d.getDate()).padStart(2,"0")}/${String(d.getMonth()+1).padStart(2,"0")}`;
};

const brl = (v) => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const StepDot = ({ n, current, label }) => (
    <div className="flex items-center gap-2">
        <div
            className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold border transition-all ${
                current >= n
                    ? "bg-amber-500 border-amber-400 text-slate-950 shadow-[0_0_12px_-2px_rgba(245,158,11,0.7)]"
                    : "bg-slate-900 border-white/10 text-slate-500"
            }`}
        >
            {current > n ? <i className="fa-solid fa-check text-[10px]"></i> : n}
        </div>
        <span className={`text-[11px] uppercase tracking-[0.25em] whitespace-nowrap ${current >= n ? "text-amber-200" : "text-slate-600"}`}>
            {label}
        </span>
    </div>
);

const PublicBooking = () => {
    const { shopId } = useParams();
    const [info, setInfo] = useState(null);
    const [services, setServices] = useState([]);
    const [pros, setPros] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [step, setStep] = useState(1);
    const [selectedServiceIds, setSelectedServiceIds] = useState([]);
    const [selectedProId, setSelectedProId] = useState("");
    const [date, setDate] = useState(todayISO());
    const [availableSlots, setAvailableSlots] = useState([]);
    const [slotsLoading, setSlotsLoading] = useState(false);
    const [selectedTime, setSelectedTime] = useState("");
    const [clientName, setClientName] = useState("");
    const [clientPhone, setClientPhone] = useState("");
    const [showWaitlistModal, setShowWaitlistModal] = useState(false);
    const [waitlistSuccess, setWaitlistSuccess] = useState(null);
    const [notes, setNotes] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [confirmation, setConfirmation] = useState(null);

    // Load salon meta
    useEffect(() => {
        let alive = true;
        (async () => {
            setLoading(true);
            setError(null);
            try {
                const [i, s, p] = await Promise.all([
                    publicFetch(`/public/${shopId}/info`),
                    publicFetch(`/public/${shopId}/services`),
                    publicFetch(`/public/${shopId}/professionals`),
                ]);
                if (!alive) return;
                setInfo(i);
                setServices(s || []);
                setPros(p || []);
            } catch (e) {
                if (!alive) return;
                setError(e.message || "Salão não encontrado.");
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [shopId]);

    // Load slots when date/pro change (step 3)
    useEffect(() => {
        if (step < 3 || !date) return;
        let alive = true;
        (async () => {
            setSlotsLoading(true);
            try {
                const duration = totalDuration || 30;
                const q = new URLSearchParams({ date, duration: String(duration) });
                if (selectedProId) q.set("professional_id", selectedProId);
                const res = await publicFetch(`/public/${shopId}/availability?${q.toString()}`);
                if (!alive) return;
                setAvailableSlots(res.slots || []);
                setSelectedTime((prev) => (res.slots?.includes(prev) ? prev : ""));
            } catch (e) {
                if (!alive) return;
                setAvailableSlots([]);
            } finally {
                if (alive) setSlotsLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [step, date, selectedProId, shopId, selectedServiceIds]);

    const selectedServices = useMemo(
        () => services.filter((s) => selectedServiceIds.includes(s.id)),
        [services, selectedServiceIds]
    );
    const totalPrice = useMemo(
        () => selectedServices.reduce((sum, s) => sum + Number(s.price || 0), 0),
        [selectedServices]
    );
    const totalDuration = useMemo(
        () => selectedServices.reduce((sum, s) => sum + Number(s.duration_min || 30), 0) || 30,
        [selectedServices]
    );
    const selectedPro = pros.find((p) => p.id === selectedProId);

    // Horários de funcionamento reais do salão (mesma fonte de Configurações).
    const salonHours = info?.brand?.salonHours;
    const openDays = useMemo(() => {
        const all = nextDays(21);
        const open = all.filter((d) => isDayOpen(salonHours, d));
        // Se o salão estiver (mal-)configurado como fechado todo dia, não trava
        // o fluxo — mostra os próximos 14 dias como fallback.
        return (open.length > 0 ? open : all).slice(0, 14);
    }, [salonHours]);

    // Garante que a data selecionada é sempre um dia com expediente.
    useEffect(() => {
        if (openDays.length === 0) return;
        const validISOs = openDays.map(toISO);
        setDate((prev) => (validISOs.includes(prev) ? prev : validISOs[0]));
    }, [openDays]);

    const toggleService = (id) => {
        setSelectedServiceIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    };

    const canNext = () => {
        if (step === 1) return selectedServiceIds.length > 0;
        if (step === 2) return true; // pro is optional
        if (step === 3) return !!date && !!selectedTime;
        if (step === 4) return clientName.trim().length >= 2 && clientPhone.trim().length >= 8;
        return false;
    };

    const submit = async () => {
        setSubmitting(true);
        try {
            const res = await publicFetch(`/public/${shopId}/booking`, {
                method: "POST",
                body: JSON.stringify({
                    client_name: clientName.trim(),
                    client_phone: clientPhone.trim(),
                    service_ids: selectedServiceIds,
                    professional_id: selectedProId || null,
                    date,
                    time: selectedTime,
                    duration_min: totalDuration,
                    notes: notes.trim(),
                }),
            });
            setConfirmation({
                id: res.appointment_id,
                shopName: info?.shop_name,
                date, time: selectedTime,
                services: selectedServices,
                pro: selectedPro,
                total: totalPrice,
                client: clientName,
            });
            toast.success("Solicitação enviada!", { description: "O salão vai confirmar em instantes." });
        } catch (e) {
            toast.error("Não foi possível enviar", { description: e.message });
        } finally {
            setSubmitting(false);
        }
    };

    // ─── UI ───────────────────────────────────────────────────────────
    if (loading) {
        return (
            <div className="min-h-screen bg-slate-950 text-slate-300 flex items-center justify-center">
                <div className="text-center">
                    <div className="inline-block h-10 w-10 border-2 border-amber-500 border-r-transparent rounded-full animate-spin mb-4"></div>
                    <p className="text-sm uppercase tracking-[0.3em] text-slate-500">Carregando salão…</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-slate-950 text-slate-300 flex items-center justify-center px-6">
                <div className="max-w-md text-center border border-red-500/30 bg-red-950/20 rounded-2xl p-8">
                    <i className="fa-solid fa-triangle-exclamation text-4xl text-red-400 mb-4"></i>
                    <h1 className="salon-heading text-2xl text-white mb-2">Salão não encontrado</h1>
                    <p className="text-sm text-slate-400 mb-4">{error}</p>
                    <p className="text-xs text-slate-600">Verifique o link recebido ou peça um novo ao estabelecimento.</p>
                </div>
            </div>
        );
    }

    if (confirmation) {
        const waMsg = encodeURIComponent(
            `Olá! Acabei de solicitar um agendamento em ${confirmation.shopName} para ${confirmation.date} às ${confirmation.time}. Podem confirmar? — ${confirmation.client}`
        );
        return (
            <div className="min-h-screen bg-slate-950 text-slate-200 flex items-center justify-center px-6 py-10">
                <div className="max-w-lg w-full border border-emerald-500/30 bg-emerald-950/10 rounded-3xl p-8 shadow-[0_0_60px_-15px_rgba(16,185,129,0.4)]" data-testid="booking-success">
                    <div className="h-16 w-16 rounded-full bg-emerald-500/15 border border-emerald-400/40 flex items-center justify-center mb-5">
                        <i className="fa-solid fa-check text-3xl text-emerald-300"></i>
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-300 mb-1">Solicitação enviada</div>
                    <h1 className="salon-heading text-3xl text-white mb-1">Aguardando confirmação</h1>
                    <p className="text-sm text-slate-400 mb-6">
                        {confirmation.shopName} recebeu sua solicitação e vai retornar em instantes com a confirmação do horário.
                    </p>
                    <div className="rounded-2xl border border-white/10 bg-slate-900/60 p-4 space-y-2 text-sm mb-6">
                        <div className="flex justify-between"><span className="text-slate-500">Data</span><span className="font-mono">{confirmation.date}</span></div>
                        <div className="flex justify-between"><span className="text-slate-500">Horário</span><span className="font-mono text-amber-200">{confirmation.time}</span></div>
                        {confirmation.pro && (
                            <div className="flex justify-between"><span className="text-slate-500">Profissional</span><span>{confirmation.pro.name}</span></div>
                        )}
                        <div className="flex justify-between"><span className="text-slate-500">Serviços</span><span className="text-right">{confirmation.services.map(s => s.name).join(", ")}</span></div>
                        <div className="flex justify-between border-t border-white/5 pt-2 mt-2"><span className="text-slate-400">Total previsto</span><span className="font-mono text-emerald-300">{brl(confirmation.total)}</span></div>
                    </div>
                    <div className="flex flex-col gap-2">
                        <a
                            data-testid="booking-success-whatsapp"
                            href={`https://wa.me/?text=${waMsg}`}
                            target="_blank"
                            rel="noreferrer"
                            className="w-full text-center rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold py-3 transition-all"
                        >
                            <i className="fa-brands fa-whatsapp mr-2"></i>Falar com o salão
                        </a>
                        <button
                            data-testid="booking-success-new"
                            onClick={() => { setConfirmation(null); setStep(1); setSelectedServiceIds([]); setSelectedTime(""); setClientName(""); setClientPhone(""); setNotes(""); }}
                            className="w-full text-center rounded-xl border border-white/10 hover:border-amber-500/40 text-slate-300 hover:text-amber-200 py-3 text-sm transition-all"
                        >
                            Fazer outra solicitação
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-950 text-slate-200 pb-20" data-testid="public-booking-page">
            {/* Header */}
            <header className="border-b border-white/5 bg-slate-950/80 backdrop-blur-xl sticky top-0 z-10">
                <div className="max-w-3xl mx-auto px-5 py-4 flex items-center justify-between">
                    <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500">Agende online</div>
                        <h1 className="salon-heading text-xl text-white truncate" data-testid="booking-shop-name">{info?.shop_name}</h1>
                    </div>
                    <div className="text-right">
                        <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">via</div>
                        <div className="text-xs font-mono text-amber-200">kinng.app</div>
                    </div>
                </div>
                {/* Stepper */}
                <div className="max-w-3xl mx-auto px-5 pb-3 flex items-center gap-3 overflow-x-auto">
                    <StepDot n={1} current={step} label="Serviço" />
                    <div className="h-px w-6 bg-white/10 shrink-0" />
                    <StepDot n={2} current={step} label="Profissional" />
                    <div className="h-px w-6 bg-white/10 shrink-0" />
                    <StepDot n={3} current={step} label="Horário" />
                    <div className="h-px w-6 bg-white/10 shrink-0" />
                    <StepDot n={4} current={step} label="Contato" />
                </div>
            </header>

            {/* Content */}
            <main className="max-w-3xl mx-auto px-5 py-6">
                {/* Step 1 · Services */}
                {step === 1 && (
                    <section data-testid="booking-step-services">
                        <h2 className="salon-heading text-2xl text-white mb-1">Selecione os serviços</h2>
                        <p className="text-sm text-slate-500 mb-5">Escolha um ou mais serviços que deseja realizar.</p>
                        {services.length === 0 ? (
                            <div className="text-center py-16 text-slate-500 border border-dashed border-white/10 rounded-2xl">
                                Este salão ainda não cadastrou serviços disponíveis online.
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {services.map((s) => {
                                    const active = selectedServiceIds.includes(s.id);
                                    return (
                                        <button
                                            key={s.id}
                                            data-testid={`booking-service-${s.id}`}
                                            onClick={() => toggleService(s.id)}
                                            className={`text-left rounded-2xl border p-4 transition-all ${
                                                active
                                                    ? "border-amber-400/70 bg-amber-500/10 shadow-[0_0_20px_-8px_rgba(245,158,11,0.6)]"
                                                    : "border-white/10 bg-slate-900/40 hover:border-amber-500/30"
                                            }`}
                                        >
                                            <div className="flex items-start justify-between gap-2 mb-1">
                                                <span className={`font-medium ${active ? "text-white" : "text-slate-200"}`}>{s.name}</span>
                                                <span className={`h-5 w-5 rounded-full border flex items-center justify-center shrink-0 ${active ? "bg-amber-400 border-amber-300 text-slate-950" : "border-white/20"}`}>
                                                    {active && <i className="fa-solid fa-check text-[10px]"></i>}
                                                </span>
                                            </div>
                                            <div className="flex items-center justify-between text-xs text-slate-500">
                                                <span>{s.duration_min || 30} min</span>
                                                <span className="font-mono text-emerald-300">{brl(s.price)}</span>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </section>
                )}

                {/* Step 2 · Professional */}
                {step === 2 && (
                    <section data-testid="booking-step-professional">
                        <h2 className="salon-heading text-2xl text-white mb-1">Escolha o profissional</h2>
                        <p className="text-sm text-slate-500 mb-5">Prefere alguém específico? Ou deixe o salão decidir.</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <button
                                data-testid="booking-pro-any"
                                onClick={() => setSelectedProId("")}
                                className={`text-left rounded-2xl border p-4 transition-all ${
                                    selectedProId === ""
                                        ? "border-amber-400/70 bg-amber-500/10"
                                        : "border-white/10 bg-slate-900/40 hover:border-amber-500/30"
                                }`}
                            >
                                <div className="flex items-center gap-3">
                                    <div className="h-10 w-10 rounded-full bg-slate-800 border border-white/10 flex items-center justify-center text-slate-400">
                                        <i className="fa-solid fa-shuffle"></i>
                                    </div>
                                    <div>
                                        <div className="font-medium text-white">Sem preferência</div>
                                        <div className="text-xs text-slate-500">O salão escolhe o melhor disponível</div>
                                    </div>
                                </div>
                            </button>
                            {pros.map((p) => {
                                const active = selectedProId === p.id;
                                return (
                                    <button
                                        key={p.id}
                                        data-testid={`booking-pro-${p.id}`}
                                        onClick={() => setSelectedProId(p.id)}
                                        className={`text-left rounded-2xl border p-4 transition-all ${
                                            active
                                                ? "border-amber-400/70 bg-amber-500/10"
                                                : "border-white/10 bg-slate-900/40 hover:border-amber-500/30"
                                        }`}
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className="h-10 w-10 rounded-full bg-slate-800 border border-white/10 flex items-center justify-center text-amber-200 font-bold">
                                                {(p.name || "?").slice(0,1).toUpperCase()}
                                            </div>
                                            <div className="min-w-0">
                                                <div className="font-medium text-white truncate">{p.name}</div>
                                                <div className="text-xs text-slate-500 truncate">{p.role || "Profissional"}</div>
                                            </div>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </section>
                )}

                {/* Step 3 · Date + Time */}
                {step === 3 && (
                    <section data-testid="booking-step-datetime">
                        <h2 className="salon-heading text-2xl text-white mb-1">Escolha data e horário</h2>
                        <p className="text-sm text-slate-500 mb-5">Duração estimada: <span className="text-amber-200 font-mono">{totalDuration} min</span></p>

                        {/* Date strip — só dias com expediente configurado */}
                        <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500 mb-2">Data</div>
                        <div className="flex gap-2 overflow-x-auto pb-2 mb-5 -mx-1 px-1">
                            {openDays.map((d) => {
                                const iso = toISO(d);
                                const active = date === iso;
                                return (
                                    <button
                                        key={iso}
                                        data-testid={`booking-date-${iso}`}
                                        onClick={() => setDate(iso)}
                                        className={`shrink-0 rounded-xl border px-3 py-2 text-xs whitespace-nowrap transition-all ${
                                            active
                                                ? "border-amber-400/70 bg-amber-500/15 text-amber-100"
                                                : "border-white/10 bg-slate-900/40 text-slate-400 hover:border-amber-500/30"
                                        }`}
                                    >
                                        {fmtDayShort(d)}
                                    </button>
                                );
                            })}
                        </div>

                        {/* Slots */}
                        <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500 mb-2">Horários livres</div>
                        {slotsLoading ? (
                            <div className="text-center py-10 text-slate-500 text-sm">
                                <div className="inline-block h-6 w-6 border-2 border-amber-500 border-r-transparent rounded-full animate-spin mb-2"></div>
                                <div>Buscando disponibilidade…</div>
                            </div>
                        ) : availableSlots.length === 0 ? (
                            <div data-testid="booking-empty-slots" className="text-center py-8 border border-dashed border-white/10 rounded-2xl px-4">
                                <div className="text-4xl mb-2">😕</div>
                                <div className="text-sm text-slate-300 mb-4">Não há horários disponíveis neste período.</div>
                                <div className="flex flex-col sm:flex-row gap-2 justify-center">
                                    <button
                                        data-testid="booking-join-waitlist-btn"
                                        onClick={() => setShowWaitlistModal(true)}
                                        className="min-h-[44px] px-5 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 text-xs font-bold uppercase tracking-[0.2em] transition-all"
                                    >
                                        <i className="fa-solid fa-bell mr-2"></i>Entrar na lista de espera
                                    </button>
                                    <button
                                        data-testid="booking-choose-other-day"
                                        onClick={() => document.querySelector('input[type="date"]')?.focus()}
                                        className="min-h-[44px] px-5 rounded-xl border border-white/10 text-slate-300 hover:text-white hover:border-white/25 text-xs font-semibold uppercase tracking-[0.2em]"
                                    >
                                        <i className="fa-solid fa-arrow-left mr-2"></i>Escolher outro dia
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                                {availableSlots.map((slot) => {
                                    const active = selectedTime === slot;
                                    return (
                                        <button
                                            key={slot}
                                            data-testid={`booking-slot-${slot}`}
                                            onClick={() => setSelectedTime(slot)}
                                            className={`rounded-lg border py-2 text-sm font-mono transition-all ${
                                                active
                                                    ? "border-amber-400/70 bg-amber-500/20 text-amber-100 shadow-[0_0_12px_-4px_rgba(245,158,11,0.6)]"
                                                    : "border-white/10 bg-slate-900/40 text-slate-300 hover:border-amber-500/30"
                                            }`}
                                        >
                                            {slot}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </section>
                )}

                {/* Step 4 · Contact */}
                {step === 4 && (
                    <section data-testid="booking-step-contact">
                        <h2 className="salon-heading text-2xl text-white mb-1">Seus dados</h2>
                        <p className="text-sm text-slate-500 mb-5">Como o salão pode entrar em contato para confirmar?</p>

                        <div className="space-y-3">
                            <div>
                                <label className="text-[10px] uppercase tracking-[0.28em] text-slate-500 mb-1 block">Nome completo</label>
                                <input
                                    data-testid="booking-input-name"
                                    value={clientName}
                                    onChange={(e) => setClientName(e.target.value)}
                                    placeholder="Ex: Ana Beatriz"
                                    className="w-full bg-slate-900/60 border border-white/10 focus:border-amber-500/40 rounded-xl px-4 py-3 text-white placeholder:text-slate-600 outline-none transition-all"
                                />
                            </div>
                            <div>
                                <label className="text-[10px] uppercase tracking-[0.28em] text-slate-500 mb-1 block">WhatsApp</label>
                                <input
                                    data-testid="booking-input-phone"
                                    value={clientPhone}
                                    onChange={(e) => setClientPhone(e.target.value)}
                                    placeholder="(11) 91234-5678"
                                    className="w-full bg-slate-900/60 border border-white/10 focus:border-amber-500/40 rounded-xl px-4 py-3 text-white placeholder:text-slate-600 outline-none transition-all font-mono"
                                />
                            </div>
                            <div>
                                <label className="text-[10px] uppercase tracking-[0.28em] text-slate-500 mb-1 block">Observações (opcional)</label>
                                <textarea
                                    data-testid="booking-input-notes"
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    rows={3}
                                    placeholder="Ex: sou alérgico a amônia, prefiro tesoura, etc."
                                    className="w-full bg-slate-900/60 border border-white/10 focus:border-amber-500/40 rounded-xl px-4 py-3 text-white placeholder:text-slate-600 outline-none transition-all resize-none"
                                />
                            </div>
                        </div>

                        {/* Summary card */}
                        <div className="mt-6 rounded-2xl border border-amber-500/20 bg-amber-500/5 p-4 space-y-1 text-sm">
                            <div className="text-[10px] uppercase tracking-[0.28em] text-amber-300 mb-2">Resumo da solicitação</div>
                            <div className="flex justify-between"><span className="text-slate-400">Serviços</span><span className="text-right text-slate-100">{selectedServices.map(s => s.name).join(", ")}</span></div>
                            <div className="flex justify-between"><span className="text-slate-400">Profissional</span><span>{selectedPro?.name || "Sem preferência"}</span></div>
                            <div className="flex justify-between"><span className="text-slate-400">Data</span><span className="font-mono">{date}</span></div>
                            <div className="flex justify-between"><span className="text-slate-400">Horário</span><span className="font-mono text-amber-200">{selectedTime}</span></div>
                            <div className="flex justify-between border-t border-white/5 pt-2 mt-2"><span className="text-slate-300">Total previsto</span><span className="font-mono text-emerald-300 font-bold">{brl(totalPrice)}</span></div>
                        </div>
                    </section>
                )}
            </main>

            <PublicFooter />

            {/* Sticky footer / nav */}
            <footer className="fixed bottom-0 inset-x-0 border-t border-white/5 bg-slate-950/95 backdrop-blur-xl z-20">
                <div className="max-w-3xl mx-auto px-5 py-4 flex items-center justify-between gap-3">
                    <button
                        data-testid="booking-back-btn"
                        onClick={() => setStep((s) => Math.max(1, s - 1))}
                        disabled={step === 1}
                        className="rounded-xl border border-white/10 text-slate-300 hover:text-white hover:border-white/20 px-4 py-3 text-sm disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                    >
                        <i className="fa-solid fa-arrow-left mr-2"></i>Voltar
                    </button>
                    <div className="hidden sm:block text-xs text-slate-500 font-mono truncate">
                        {selectedServiceIds.length > 0 && `${selectedServiceIds.length} serviço(s)`}
                        {selectedTime && ` · ${selectedTime}`}
                        {totalPrice > 0 && ` · ${brl(totalPrice)}`}
                    </div>
                    {step < 4 ? (
                        <button
                            data-testid="booking-next-btn"
                            onClick={() => setStep((s) => s + 1)}
                            disabled={!canNext()}
                            className="rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold px-6 py-3 text-sm disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                        >
                            Continuar<i className="fa-solid fa-arrow-right ml-2"></i>
                        </button>
                    ) : (
                        <button
                            data-testid="booking-submit-btn"
                            onClick={submit}
                            disabled={!canNext() || submitting}
                            className="rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold px-6 py-3 text-sm disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                        >
                            {submitting ? (
                                <><i className="fa-solid fa-spinner fa-spin mr-2"></i>Enviando…</>
                            ) : (
                                <><i className="fa-solid fa-paper-plane mr-2"></i>Enviar solicitação</>
                            )}
                        </button>
                    )}
                </div>
            </footer>

            {showWaitlistModal && !waitlistSuccess && (
                <WaitlistJoinModal
                    shopId={shopId}
                    shopName={info?.shop_name || shopId}
                    services={services}
                    selectedServiceIds={selectedServiceIds}
                    selectedProId={selectedProId}
                    proName={pros.find((p) => p.id === selectedProId)?.name || "Qualquer profissional"}
                    initialName={clientName}
                    initialPhone={clientPhone}
                    onClose={() => setShowWaitlistModal(false)}
                    onSuccess={(data) => { setWaitlistSuccess(data); setShowWaitlistModal(false); }}
                />
            )}
            {waitlistSuccess && (
                <div data-testid="booking-waitlist-success" className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="max-w-md w-full rounded-3xl bg-slate-900 border border-emerald-500/30 p-8 text-center">
                        <div className="text-5xl mb-3">✅</div>
                        <h3 className="salon-heading text-2xl text-white mb-3">Você está na lista de espera!</h3>
                        <p className="text-sm text-slate-300 leading-relaxed">
                            Assim que abrir um horário, você receberá um <span className="text-emerald-300 font-semibold">WhatsApp</span> no número{" "}
                            <span className="text-white font-semibold">{clientPhone || waitlistSuccess.phone}</span>.
                        </p>
                        <p className="text-xs text-slate-500 mt-3">Você terá 30 minutos para confirmar após ser notificado.</p>
                        <button onClick={() => { setWaitlistSuccess(null); }}
                                className="mt-6 w-full h-12 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-xs uppercase tracking-widest">
                            OK
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

// ─── Modal: entrar na lista de espera ─────────────────────────────────────
const WaitlistJoinModal = ({ shopId, shopName, services, selectedServiceIds, selectedProId, proName,
                              initialName, initialPhone, onClose, onSuccess }) => {
    const [name, setName] = useState(initialName || "");
    const [phone, setPhone] = useState(initialPhone || "");
    const [period, setPeriod] = useState("any");
    const [saving, setSaving] = useState(false);

    const svcNames = services
        .filter((s) => selectedServiceIds.includes(s.id))
        .map((s) => s.name)
        .join(" + ") || "Serviço a definir";

    const submit = async (e) => {
        e.preventDefault();
        if (!name.trim() || !phone.trim()) return toast.error("Nome e telefone são obrigatórios");
        setSaving(true);
        try {
            const data = await publicFetch(`/public/${shopId}/waitlist`, {
                method: "POST",
                body: JSON.stringify({
                    client_name: name.trim(), client_phone: phone.trim(),
                    service_ids: selectedServiceIds,
                    professional_id: selectedProId || null,
                    preferred_period: period, preferred_days: [], notes: "",
                }),
            });
            onSuccess(data);
        } catch (err) { toast.error("Falha", { description: err.message }); }
        finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
            <form data-testid="booking-waitlist-modal" onClick={(e) => e.stopPropagation()} onSubmit={submit}
                  className="max-w-md w-full rounded-3xl bg-slate-900 border border-amber-500/30 p-6">
                <h3 className="salon-heading text-xl text-white mb-1">Entrar na lista de espera</h3>
                <p className="text-xs text-slate-400 mb-4">Vamos te avisar assim que abrir um horário</p>

                <div className="rounded-xl border border-white/5 bg-slate-950/60 p-3 mb-4 text-xs space-y-1">
                    <div className="text-slate-500 uppercase tracking-widest text-[10px]">Resumo</div>
                    <div className="text-slate-200"><span className="text-slate-500">Salão:</span> {shopName}</div>
                    <div className="text-slate-200"><span className="text-slate-500">Serviço:</span> {svcNames}</div>
                    <div className="text-slate-200"><span className="text-slate-500">Profissional:</span> {proName}</div>
                </div>

                <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-2">
                        <input data-testid="waitlist-modal-name" placeholder="Nome" value={name}
                               onChange={(e) => setName(e.target.value)}
                               className="bg-slate-950/60 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white" />
                        <input data-testid="waitlist-modal-phone" placeholder="Telefone" value={phone}
                               onChange={(e) => setPhone(e.target.value)}
                               className="bg-slate-950/60 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white" />
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1.5">Período preferido</div>
                        <div className="flex gap-1">
                            {[
                                { id: "any", label: "Qualquer" },
                                { id: "manha", label: "Manhã" },
                                { id: "tarde", label: "Tarde" },
                                { id: "noite", label: "Noite" },
                            ].map((p) => (
                                <button type="button" key={p.id} data-testid={`waitlist-period-${p.id}`}
                                        onClick={() => setPeriod(p.id)}
                                        className={`flex-1 h-9 rounded-lg text-[10px] uppercase tracking-wider border ${period === p.id ? "bg-amber-500 text-slate-950 border-amber-400" : "border-white/10 text-slate-400"}`}>
                                    {p.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="mt-5 flex gap-2">
                    <button type="button" onClick={onClose} className="flex-1 h-11 rounded-xl border border-white/10 text-slate-400 text-xs uppercase tracking-widest">Cancelar</button>
                    <button data-testid="waitlist-modal-confirm" type="submit" disabled={saving}
                            className="flex-1 h-11 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold text-xs uppercase tracking-widest rounded-xl disabled:opacity-50">
                        {saving ? "Enviando..." : "Confirmar"}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default PublicBooking;
